// src/ui/Toast.tsx
// Pure presentational Toast component (no timers, no local state).
// - Uses only existing Tailwind tokens with "tw-" prefix
// - Supports variant styling, optional close button, left icon
// - Accepts `exiting` to apply slide-right + fade via classes (external control)
// - Accepts `bottomSlot` to render e.g. a tiny progress bar at the bottom

import * as React from "react";
import clsx from "classnames";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FaIcon, faCircleInfo, faXmark } from "@/lib/fa";

const defaultIcons: Record<ToastVariant, IconDefinition> = {
    success: faCircleInfo,
    error: faCircleInfo,
    warning: faCircleInfo,
    info: faCircleInfo,
    neutral: faCircleInfo,
};

export type ToastVariant = "info" | "success" | "warning" | "error" | "neutral";

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Visual variant; "info" maps to accent tokens */
    variant?: ToastVariant;
    /** Optional leading icon node */
    icon?: React.ReactNode;
    /** FontAwesome alternative leading icon */
    faLeadingIcon?: IconDefinition;
    /** Optional text shortcut (in addition to children) */
    text?: string;
    /** Optional title displayed above content */
    title?: string;

    /** Show the × button */
    dismissible?: boolean;
    /** Called when × is clicked (presentation only) */
    onClose?: () => void;
    /** Accessible label for the close button */
    closeLabel?: string;

    /** Keep two-column grid even without icon */
    forceGrid?: boolean;

    /** A11y */
    role?: "status" | "alert";
    ariaLive?: "polite" | "assertive" | "off";

    /** External control: if true, apply exit animation classes */
    exiting?: boolean;
    /** Provide a bottom overlay (e.g., progress bar) */
    bottomSlot?: React.ReactNode;
}

function variantClasses(variant: ToastVariant) {
    // "info" → accent tokens
    const map = {
        info: { container: "tw-bg-accent", border: "tw-border-accent", text: "tw-text-content-neutral", icon: "tw-text-content-neutral", close: "tw-text-content-neutral", track: "tw-bg-base-300", progress: "tw-bg-neutral" },
        success: { container: "tw-bg-success", border: "tw-border-success", text: "tw-text-content-neutral", icon: "tw-text-content-success", close: "tw-text-content-neutral", track: "tw-bg-base-300", progress: "tw-bg-neutral" },
        warning: { container: "tw-bg-warning", border: "tw-border-warning", text: "tw-text-content-neutral", icon: "tw-text-content-warning", close: "tw-text-content-neutral", track: "tw-bg-base-300", progress: "tw-bg-neutral" },
        error: { container: "tw-bg-error", border: "tw-border-error", text: "tw-text-content-neutral", icon: "tw-text-content-error", close: "tw-text-content-neutral", track: "tw-bg-base-300", progress: "tw-bg-neutral" },
        neutral: { container: "tw-bg-content-neutral", border: "tw-border-neutral", text: "tw-text-base", icon: "tw-text-neutral", close: "tw-text-neutral", track: "tw-bg-base-300", progress: "tw-bg-neutral" },
    } as const;
    return map[variant] ?? map.info;
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(function Toast(
    {
        variant = "info",
        icon,
        faLeadingIcon = faCircleInfo,
        text,
        title,
        dismissible = true,
        onClose,
        closeLabel = "Dismiss notification",
        forceGrid = false,
        role = "status",
        ariaLive = "polite",
        exiting = false,
        bottomSlot,
        className,
        children,
        ...rest
    },
    ref
) {
    const v = variantClasses(variant);
    const gridCols =
        icon || faLeadingIcon || forceGrid
            ? "tw-grid tw-grid-cols-[auto_1fr] tw-gap-1"
            : "tw-grid tw-grid-cols-1 tw-gap-1";

    return (
        <div
            ref={ref}
            role={role}
            aria-live={ariaLive}
            aria-atomic={false}
            data-variant={variant}
            className={clsx(
                // base card
                "tw-relative", // for absolute close & bottomSlot
                "tw-pointer-events-auto tw-min-w-[260px] tw-max-w-[420px]",
                "tw-border tw-shadow-xl tw-backdrop-saturate-150",
                "tw-p-2",
                // motion (external control via `exiting`)
                "tw-transform tw-transition tw-ease-out",
                exiting ? "tw-opacity-0 tw-translate-x-4" : "tw-opacity-100 tw-translate-x-0",
                // layout
                gridCols,
                "tw-items-start",
                // tokens
                v.container,
                v.border,
                v.text,
                dismissible ? 'tw-' : '',
                className,
            )}
            {...rest}
        >
            {/* leading icon */}
            {icon ? (
                <div aria-hidden className={clsx("tw-text-base tw-leading-none tw-mt-[2px]", v.icon)}>
                    {icon}
                </div>
            ) : faLeadingIcon ? (
                <FaIcon className={clsx("tw-text-base tw-leading-none tw-m-1 tw-w-[16px] tw-h-[16px]", v.icon)} icon={faLeadingIcon} />
            ) : null}

            {/* content */}
            <div className={clsx("tw-flex tw-flex-col tw-gap-0.5 tw-text-sm tw-leading-tight", dismissible ? 'tw-mr-[40px]' : '')}>
                {title && <div className="tw-font-semibold tw-text-base tw-mb-0.5">{title}</div>}
                {children}
                {text}
            </div>

            {/* close button (absolute top-right) */}
            {dismissible && (
                <button
                    type="button"
                    aria-label={closeLabel}
                    onClick={onClose}
                    className={clsx(
                        "tw-absolute tw-top-2 tw-right-2",
                        "tw-appearance-none tw-border-0 tw-bg-transparent",
                        "tw-text-base tw-leading-none tw-cursor-pointer",
                        "hover:tw-opacity-80 focus:tw-outline-none",
                        "focus:tw-ring-2 focus:tw-ring-focus-100",
                        "focus:tw-ring-offset-2 focus:tw-ring-offset-focus-200",
                        v.close
                    )}
                >
                    <FaIcon icon={faXmark} className="tw-w-[20px] tw-h-[20px]" />
                </button>
            )}

            {/* bottom overlay slot (e.g., progress bar) */}
            {bottomSlot}
        </div>
    );
});
